/**
 * Fal.ai API Service
 * Handles AI-powered design generation using Flux-2-Pro Edit model
 *
 * Security: API calls are proxied through backend API to keep API key secret
 * Endpoint: /api/generate-design (backend) → https://fal.run/fal-ai/flux-2-pro/edit
 *
 * Prompt Architecture:
 * Unified (display sentence): UNIFIED_PREFIX + filled Mad Libs sentence
 * All templates and layouts have display sentences — no fallback paths.
 */

import type { TemplateConfig, LayoutVariant } from '@/types';
import { getDisplaySentence } from '@/features/ai/data/displaySentences';
import { fillSentence } from '@/features/ai/utils/sentenceParser';
import { withRetry, isRetryableFalError } from '@/utils/retry';

export interface GenerateDesignParams {
  /** Product template image (base64 or URL) - may be overridden by template */
  productImageUrl: string;
  /** User-uploaded reference image (base64 or URL, optional) */
  referenceImageUrl?: string;
  /** Selected art style label (e.g., 'Line Art', 'Engraving Style') */
  style: string;
  /** Selected template configuration with prompt settings */
  template: TemplateConfig;
  /** Selected mood label (e.g., 'Gothic', 'Peaceful') */
  mood: string;
  /** Decorative elements to include (max 3) */
  elements: string[];
  /** Noun slot values keyed by slot ID (e.g. {'noun-1': 'doves'}) */
  nouns?: Record<string, string>;
  /** Text to engrave (max 75 characters) */
  text: string;
  /** Selected layout variant (optional, only for templates with layouts) */
  selectedLayout?: LayoutVariant;
  /** Product-specific image dimensions for Fal.ai (defaults to 450x777) */
  imageSize?: { width: number; height: number };
}

export interface PromptGenerationResult {
  prompt: string;
  strength: number;
  metadata: {
    templateId: string;
    prefixLength: number;
    sentenceLength: number;
    totalLength: number;
  };
}

export interface GenerateDesignResult {
  imageUrl: string;
  seed: number;
  hasNsfwContent: boolean;
}

/**
 * Build a Map of slot values from generation params
 * Maps slot IDs (style-1, mood-1, text-1, elements-1) to their current values
 */
function buildSlotValues(params: GenerateDesignParams): Map<string, string> {
  const values = new Map<string, string>();
  values.set('style-1', params.style);
  values.set('mood-1', params.mood);
  if (params.text) {
    values.set('text-1', params.text);
  }
  if (params.elements.length > 0) {
    values.set('elements-1', params.elements.join(', '));
  }
  if (params.nouns) {
    for (const [slotId, value] of Object.entries(params.nouns)) {
      if (value) values.set(slotId, value);
    }
  }
  return values;
}

// ============================================================================
// UNIFIED PROMPT ARCHITECTURE
// Single sentence serves as both Mad Libs display and Fal.ai prompt
// Format: UNIFIED_PREFIX + filled_sentence
// ============================================================================

const UNIFIED_PREFIX = 'Use Image 1 as a base image for the format, Only edit the black area. ';

/**
 * Strip "from Image 2" references when no reference image is uploaded.
 * Keeps the rest of the composition description intact.
 */
export function stripImage2Reference(sentence: string): string {
  return sentence
    .replace(/\s*from Image 2/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * Strip the "Display...''" text clause when text slot was not filled.
 * Removes the entire "Display...- exact spelling, no other text" phrase.
 */
function stripEmptyTextClause(sentence: string): string {
  return sentence
    .replace(/\.\s*Display[^']*''\s*-\s*exact spelling,\s*no other text/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * Build a unified prompt: UNIFIED_PREFIX + filled display sentence.
 * Returns null if no display sentence exists for the given template/layout.
 */
export function buildUnifiedPrompt(params: GenerateDesignParams): {
  prompt: string;
  sections: { prefix: string; sentence: string };
} | null {
  const sentence = getDisplaySentence(params.template.id, params.selectedLayout?.id);
  if (!sentence) return null;

  const slotValues = buildSlotValues(params);
  let filled = fillSentence(sentence, slotValues);

  // For custom-prompt templates: user text becomes the creative direction
  if (params.template.customPromptMaxLength && params.text) {
    const refImageClause = params.referenceImageUrl
      ? ' Use Image 2 as visual reference for the design.'
      : '';
    filled = `${params.text}. ${params.style} style, ${params.mood} mood.${refImageClause}`;
  }

  // Strip Image 2 references when no reference image uploaded
  if (!params.referenceImageUrl) {
    filled = stripImage2Reference(filled);
  }

  // Handle empty text: strip text display clause and add no-text directive
  if (!params.text && !params.template.customPromptMaxLength) {
    filled = stripEmptyTextClause(filled);
    filled += ". Don't include any words or text.";
  }

  const prompt = UNIFIED_PREFIX + filled;
  return { prompt, sections: { prefix: UNIFIED_PREFIX, sentence: filled } };
}

/**
 * Build prompt with full metadata for debugging.
 * All templates have display sentences — throws if one is missing.
 */
export function buildPrompt(params: GenerateDesignParams): PromptGenerationResult {
  const unified = buildUnifiedPrompt(params);
  if (!unified) {
    throw new Error(
      `No display sentence for template "${params.template.id}"` +
        (params.selectedLayout ? `, layout "${params.selectedLayout.id}"` : '')
    );
  }

  const strength = params.selectedLayout?.strength ?? params.template.strength;
  return {
    prompt: unified.prompt,
    strength,
    metadata: {
      templateId: params.template.id,
      prefixLength: unified.sections.prefix.length,
      sentenceLength: unified.sections.sentence.length,
      totalLength: unified.prompt.length,
    },
  };
}

/**
 * Build the full API payload for Fal.ai (excluding API key)
 * Used by debug panel to show developers the exact request that will be sent
 * Strength is dynamic based on template configuration
 */
export function buildApiPayload(params: GenerateDesignParams) {
  const { prompt, strength } = buildPrompt(params);

  // Build image_urls array: @image1 = product template, @image2 = reference image (optional)
  const imageUrls = [params.productImageUrl];
  if (params.referenceImageUrl) {
    imageUrls.push(params.referenceImageUrl);
  }

  return {
    image_urls: imageUrls,
    prompt,
    image_size: params.imageSize ?? { width: 450, height: 777 },
    strength, // Not sent to flux-2-pro/edit (handled internally), but used for logging/tracking
    enable_safety_checker: true,
    safety_tolerance: '2',
    output_format: 'png',
  };
}

/**
 * Generate a laser engraving design using backend API
 * (API key kept secret on server-side for security)
 *
 * @param params The parameters for the design generation
 * @returns A promise that resolves to the generated design
 * @throws Error if API request fails
 */
export async function generateDesign(params: GenerateDesignParams): Promise<GenerateDesignResult> {
  const { prompt, strength, metadata } = buildPrompt(params);

  // Build image_urls array: @image1 = product template, @image2 = reference image (optional)
  const imageUrls = [params.productImageUrl];
  if (params.referenceImageUrl) {
    imageUrls.push(params.referenceImageUrl);
  }

  console.warn('[FAL.AI] Generating design with prompt:', prompt);
  console.warn('[FAL.AI] Template:', metadata.templateId, '| Strength:', strength);
  console.warn('[FAL.AI] Product image URL:', params.productImageUrl);
  if (params.referenceImageUrl) {
    console.warn('[FAL.AI] Reference image URL:', params.referenceImageUrl);
  }

  try {
    // Call backend API instead of Fal.ai directly (keeps API key secure)
    // Wrapped in withRetry for transient failures (429, 500, 502, 503)
    const result = await withRetry(
      async () => {
        const response = await fetch('/api/generate-design', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            image_urls: imageUrls,
            prompt,
            image_size: params.imageSize ?? { width: 450, height: 777 },
            num_inference_steps: 28,
            guidance_scale: 6,
            strength, // Dynamic strength from template
            enable_safety_checker: true,
            safety_tolerance: '2',
            output_format: 'png',
          }),
        });

        if (!response.ok) {
          // Parse error from backend
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          const statusCode = response.status;

          // Throw error with status code for proper error handling
          throw new Error(`API error status ${statusCode}: ${errorData.error || 'Request failed'}`);
        }

        return response.json();
      },
      { shouldRetry: isRetryableFalError }
    );

    console.warn('[FAL.AI] Generation complete:', result);

    // Type assertion for the result - flux-2/edit wraps response in data property
    const apiResult = result as {
      data?: {
        images?: Array<{ url: string }>;
        seed?: number;
        has_nsfw_concepts?: boolean[];
      };
      images?: Array<{ url: string }>;
      seed?: number;
      has_nsfw_concepts?: boolean[];
    };

    // Check for wrapped response (result.data.images) or direct response (result.images)
    const images = apiResult.data?.images ?? apiResult.images;
    const seed = apiResult.data?.seed ?? apiResult.seed ?? 0;
    const hasNsfwConcepts = apiResult.data?.has_nsfw_concepts ?? apiResult.has_nsfw_concepts;

    console.warn('[FAL.AI] Parsed images:', images);
    console.warn('[FAL.AI] Image URL:', images?.[0]?.url);
    console.warn('[FAL.AI] NSFW concepts:', hasNsfwConcepts);

    if (!images || images.length === 0) {
      throw new Error('No image returned from API');
    }

    const resultData = {
      imageUrl: images[0]?.url ?? '',
      seed,
      hasNsfwContent: hasNsfwConcepts?.some(Boolean) ?? false,
    };

    console.warn('[FAL.AI] Returning result:', resultData);

    return resultData;
  } catch (error) {
    console.error('[FAL.AI] Generation failed:', error);

    // Parse API errors and throw with status codes
    if (error instanceof Error) {
      // Check if this is a Fal ApiError with status code
      const apiError = error as Error & { status?: number; statusCode?: number };
      if (apiError.status || apiError.statusCode) {
        const statusCode = apiError.status || apiError.statusCode;
        throw new Error(`API error status ${statusCode}: ${error.name}`);
      }

      // Check if error message contains status code
      const statusMatch = error.message.match(/status.*?(\d{3})/i);
      if (statusMatch && statusMatch[1]) {
        const statusCode = parseInt(statusMatch[1], 10);
        throw new Error(`API error status ${statusCode}: ${error.message}`);
      }

      // For generic API errors, throw with a clear message
      if (error.name === 'ApiError' || error.message.includes('401')) {
        throw new Error('API error status 401: Authentication failed');
      }

      // Re-throw original error
      throw error;
    }
    throw new Error('Failed to generate design. Please try again.');
  }
}

/**
 * Error messages for user display
 */
export const FAL_ERROR_MESSAGES: Record<number | string, string> = {
  401: 'Unable to connect to AI service',
  429: 'Please wait a moment and try again',
  413: 'Image too large. Please use a smaller image.',
  500: 'AI service temporarily unavailable',
  timeout: 'Generation taking longer than expected',
  nsfw: 'Image could not be generated',
};
